import Table from '@/components/table/Table';
import { ADDITIONAL_REQUEST_PAYMENT_TYPE, DEFAULT_SELECT_PAGE_SIZES, DISPPLAY_MOMENT_YMD_15H, DISPPLAY_MOMENT_YMD_HM, PAYMENT_TYPE_OBJECT } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { AddLatePayment } from '@/types/dashboard';
import { PAYMENT_TYPE } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { getLinkAccountDetail, getLinkBikeDetail, getLinkLocationDetail, getLinkOrderDetail } from '@/utils/utils';
import { ColumnDef, TableState } from '@tanstack/react-table';
import { Updater } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default observer(function AddLatePayment() {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        dashboardStore: {
            addLatePayments,
            pagingAddLatePayments,
            totalAddLatePayments,
            getAddLatePayments,
            cleanAddLatePayments
        }
    } = useStore();

    // life cycle
    useEffect(() => {
        getAddLatePayments();

        return () => {
            cleanAddLatePayments();
        };
    }, []);

    // columns
    const columns: ColumnDef<AddLatePayment>[] = [
        {
            accessorKey: 'orderCodeSystem',
            header: t('field.reservation_code'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <span>
                    {original.orderCodeSystem?.length > 0 && (
                        <Link
                            to={getLinkOrderDetail(original.orderId)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.orderCodeSystem}
                        </Link>
                    )}
                </span>
            )
        },
        {
            accessorKey: 'location',
            header: t('field.location_name'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div
                    className='break-all min-w-[80px] line-clamp-2'
                >
                    {original.location?.name?.length > 0 && (
                        <Link
                            to={getLinkLocationDetail(original.location.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.location?.name}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'paymentType',
            header: t('field.dashboard.payment_type'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    <span className='whitespace-nowrap'>
                        {
                            [PAYMENT_TYPE.ORDER_CREATE, PAYMENT_TYPE.UPDATE_ORDER].includes(original?.paymentType) ?
                                t(`${PAYMENT_TYPE_OBJECT[original.paymentType].key}`) :
                                original?.paymentType === PAYMENT_TYPE.OTHER ?
                                    t(`${ADDITIONAL_REQUEST_PAYMENT_TYPE[original.additionalType].key}`) :
                                    <></>
                        }
                    </span>
                </div>
            )
        },
        {
            accessorKey: 'paymentDueDate',
            header: t('field.payment_deadline'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.paymentDueDate, DISPPLAY_MOMENT_YMD_15H, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalFrom',
            header: t('field.rental_date_and_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalTo',
            header: t('field.return_date_and_time'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'bike',
            header: t('field.model_bike'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original.bike?.bikeModel?.length > 0 && (
                        <Link
                            to={getLinkBikeDetail(original.bike.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.bike?.bikeModel}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'renter',
            header: t('field.customer_name'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.renter?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.renter.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.renter?.fullName}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'owner',
            header: t('field.owner'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.owner?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.owner.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.owner?.fullName}
                        </Link>
                    )}
                </div>
            )
        }
    ];

    // functions
    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ?
                stateUpdater(pagingAddLatePayments) :
                stateUpdater;
        getAddLatePayments({}, newPaging);
    };

    return (
        <div className='layout-card'>
            <h2 className='text-fs-mid24 leading-lh-mid24 font-medium mb-8'>
                {t('words_title.reservations_with_unpaid_additional_fees')}
            </h2>
            <Table
                columns={columns}
                data={addLatePayments}
                state={pagingAddLatePayments}
                rowCount={totalAddLatePayments}
                onStateChange={onStateChange}
                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                getRowId={row => row.id?.toString()}
                showSelectPageSize={false}
            />
        </div>
    );
});
